//* Libraries imports
import {
  SearchIcon,
  ChevronDown,
  Link as LinkIcon,
  Search,
} from "lucide-react";
import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { randomUUID } from "crypto";
import { uniqueId } from "lodash";

//* Componentes imports
import { Button } from "../../../../components/ui/button";
import { Card, CardContent, CardHeader } from "../../../../components/ui/card";
import { Input } from "../../../../components/ui/input";
import { ScrollArea, ScrollBar } from "../../../../components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "../../../../components/ui/tabs";
import {
  ProductsGridSkeleton,
  OutrosByAnimaSkeleton,
} from "../../../../components/ui/skeleton";
import { DeleteConfirmationAlert } from "../../../../components/ui/DeleteConfirmationAlert";
import { Element1ByAnima } from "./Element1ByAnima";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import { fetchProducts, deleteProduct } from "../../productSlice";
import { fetchPhaseFormById } from "../../../phaseForm/phaseFormSlice";
import { filledFormService } from "../../../filledForm/filledFormService";
import { useNotification } from "@hooks/useNotification";
import { AppointmentList } from "@/features/appointment";
import { UserList } from "@/features/user";
import { DataTable } from "@/components/ui";

//* Types imports
import type { PhaseData, ActivityData } from "./Element1ByAnima";
import { ActivityType } from "../../../Element/types";
import type { AppDispatch, RootState } from "@app/store";
import type { IProduct, IProductPhase, IActivity } from "../../types";
// Sem `type` — PhaseFormType é enum (valor em runtime), não interface.
// Quando importado como `type`, o vite/esbuild elide e vira ReferenceError
// no else "Formulário fechado, abrir modal WhatsApp" (bug do Sérgio 25/05).
import { PhaseFormType } from "../../../phaseForm/types";

// Type para o formato de card do componente
type ProductCard = {
  id: string;
  title: string;
  image: string;
  hours?: string;
  value?: string;
  stats: Array<{
    name: string;
    count: string;
    icon: string;
  }>;
};

// Produto de exemplo inicial "Geral"
const initialGeneralCard: ProductCard = {
  id: "geral",
  title: "Geral",
  image: "..//foto-7.png",
  stats: [
    { name: "Produtos", count: "06", icon: "/-cone-30.svg" },
    { name: "Fases", count: "190", icon: "/-cone.svg" },
    { name: "Atividades", count: "980", icon: "/-cone-1.svg" },
    { name: "Formulários", count: "56", icon: "/-cone-4.svg" },
    { name: "Peças Técnicas", count: "22", icon: "/-cone-4.svg" },
    { name: "Agendas", count: "08", icon: "/-cone-4.svg" },
    { name: "Colaboradores", count: "350", icon: "/-cone-4.svg" },
  ],
};

export const OutrosByAnima = (): JSX.Element => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [selectedCardId, setSelectedCardId] = useState<string>("");
  // const [selectedCardPhaseId, setSelectedCardPhaseId] = useState<string>("");
  const [selectedCardActivityId, setSelectedCardActivityId] =
    useState<string>("");

  const [selectedTab, setSelectedTab] = useState("Produtos");
  const [selectedProduct, setSelectedProduct] = useState("Produtos");

  const [selectedPhase, setSelectedPhase] = useState("Fase");
  const [selectedActivity, setSelectedActivity] = useState("Atividade");

  const [totalAppointments, setTotalAppointments] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteItemName, setDeleteItemName] = useState<string>("");
  const [filter, setFilter] = useState("");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [linkCopied, setLinkCopied] = useState<string | null>(null);

  // Estados para o modal de WhatsApp
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [currentFormId, setCurrentFormId] = useState<string | null>(null);
  const [currentProductId, setCurrentProductId] = useState<string | null>(null);
  const [allPhases, setAllPhases] = useState<PhaseData[]>([]);
  const [phaseFilter, setPhaseFilter] = useState("");

  // Estado para controlar as atividades de todos os produtos
  const [allActivities, setAllActivities] = useState<ActivityData[]>([]);
  const [activityFilter, setActivityFilter] = useState("");
  const [selectedPhaseFilter, setSelectedPhaseFilter] = useState<string>("");
  const notification = useNotification();

  // Function to format numbers to two digits
  const formatTwoDigits = (num: number | string): string => {
    const parsedNum = typeof num === "string" ? parseInt(num) : num;
    return parsedNum < 10 ? `0${parsedNum}` : `${parsedNum}`;
  };

  const handleNewClick = useCallback(() => {
    navigate("/products/new");
  }, [navigate]);

  const loadData = useCallback(async () => {
    // Avoid concurrent calls
    if (isLoadingRef.current) {
      console.log("🚫 Requisição em andamento, ignorando nova chamada");
      return;
    }

    // Avoid calls after initial load
    if (initialLoadDoneRef.current && Object.keys(filters).length === 0) {
      console.log("🚫 Ignorando chamada sem filtros após carregamento inicial");
      return;
    }

    console.log("📡 Iniciando requisição fetchProducts");
    isLoadingRef.current = true;

    try {
      await dispatch(fetchProducts());
      console.log("✅ Requisição fetchProducts completada");
      setIsInitialLoad(false);
      initialLoadDoneRef.current = true;
    } catch (error) {
      console.error("❌ Erro na requisição:", error);
    } finally {
      isLoadingRef.current = false;
    }
  }, [dispatch, filters]);

  const {
    products: items,
    loading,
    error,
  } = useSelector((state: RootState) => state.product);

  const handleFilter = useCallback((newFilters: Record<string, string>) => {
    console.log("🔍 Novos filtros aplicados:", newFilters);
    setFilters(newFilters);
  }, []);

  // Initial load effect
  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      loadData();
    }
  }, [loadData]);

  // Filter change effect
  useEffect(() => {
    if (initialLoadDoneRef.current && Object.keys(filters).length > 0) {
      loadData();
    }
  }, [filters, loadData]);

  // Mapeie os itens da API para o formato do componente
  const apiProductCards = useMemo(() => {
    if (!items || items.length === 0) return [];

    return items.map((product: IProduct) => {
      // Calcular totais de atividades
      const totalActivities =
        product.phases?.reduce(
          (sum: number, phase: IProductPhase) =>
            sum + (phase.activities?.length || 0),
          0
        ) || 0;

      const totalTechnicalPieces =
        product.phases?.reduce(
          (sum: number, phase: IProductPhase) =>
            sum +
            (phase.activities?.reduce(
              (actSum: number, activity: IActivity) =>
                actSum + (activity.techinicals?.length || 0),
              0
            ) || 0),
          0
        ) || 0;

      // Calcular duração total (horas)
      const totalDuration =
        product.phases?.reduce(
          (sum: number, phase: IProductPhase) =>
            sum +
            (phase.activities?.reduce(
              (actSum: number, activity: IActivity) =>
                actSum + (activity.duration || 0),
              0
            ) || 0),
          0
        ) || 0;

      // Valor estimado (exemplo: R$ 10 por hora)
      const estimatedValue = totalDuration * 10;
      console.log(
        "PRODUTO CARD",
        product.phases,
        selectedPhaseFilter,
        selectedPhase
      );
      const productCard: ProductCard = {
        id: product._id || product.key,
        title: product.name,
        number_product: product.number_product,

        image: (product as any).image || "..//foto-7.png", // Trata propriedade opcional com tipo any
        hours: `${totalDuration}h`,
        value: `R$ ${estimatedValue.toFixed(2).replace(".", ",")}`,
        stats: [
          {
            name: "Fases",
            count: formatTwoDigits(product.phases?.length || 0),
            icon: "/-cone.svg",
          },
          {
            name: "Atividades",
            count:
              selectedCardId == product._id
                ? formatTwoDigits(
                    product.phases?.filter(
                      (item: any) => item._id == selectedPhaseFilter
                    )[0]?.activities?.length || 0
                  )
                : formatTwoDigits(totalActivities || 0),
            icon: "/-cone-1.svg",
          },
          {
            name: "Formulários",
            count: formatTwoDigits(0),
            icon: "/-cone-4.svg",
          }, // Valor padrão
          {
            name: "Peças Técnicas",
            count:
              selectedCardId == product._id
                ? formatTwoDigits(
                    product.phases
                      ?.find((item: any) => item._id == selectedPhaseFilter)
                      ?.activities?.reduce(
                        (sum: number, activity: IActivity) =>
                          sum + (activity.techinicals?.length || 0),
                        0
                      ) || 0
                  )
                : formatTwoDigits(totalTechnicalPieces || 0),
            icon: "/-cone-4.svg",
          }, // Valor padrão
          { name: "Agendas", count: formatTwoDigits(0), icon: "/-cone-4.svg" }, // Valor padrão
          {
            name: "Colaboradores",
            count: formatTwoDigits(0),
            icon: "/-cone-4.svg",
          }, // Valor padrão
        ],
      };
      console.log("product card", productCard);

      return productCard;
    });
  }, [items, formatTwoDigits, selectedPhaseFilter]);
  console.log("selectedPhaseFilter", selectedPhaseFilter);
  // Combine os dados mockados com os dados da API
  const productCards = useMemo(() => {
    // Se temos dados da API, use-os; caso contrário, use os dados de exemplo
    const productList = [...apiProductCards];

    // Sempre adicione o card "Geral" no início
    return [initialGeneralCard, ...productList];
  }, [apiProductCards]);

  // Calcular totais para o card "Geral"
  const calculateTotals = useCallback(() => {
    const productsExceptGeral = productCards.filter(
      (card) => card.id !== "geral"
    );
    return {
      totalHours: productsExceptGeral.reduce((sum, card) => {
        const hours = card.hours ? parseInt(card.hours.replace("h", "")) : 0;
        return sum + hours;
      }, 0),
      totalValue: productsExceptGeral.reduce((sum, card) => {
        const value = card.value
          ? parseFloat(
              card.value.replace("R$ ", "").replace(".", "").replace(",", ".")
            )
          : 0;
        return sum + value;
      }, 0),
      totalProducts: formatTwoDigits(productsExceptGeral.length),
      totalPhases: formatTwoDigits(
        productsExceptGeral.reduce((sum, card) => {
          const phases =
            card.stats.find((stat) => stat.name === "Fases")?.count || "0";
          return sum + parseInt(phases);
        }, 0)
      ),
      totalActivities: formatTwoDigits(
        productsExceptGeral.reduce((sum, card) => {
          const activities =
            card.stats.find((stat) => stat.name === "Atividades")?.count || "0";
          return sum + parseInt(activities);
        }, 0)
      ),
      totalForms: formatTwoDigits(
        productsExceptGeral.reduce((sum, card) => {
          const forms =
            card.stats.find((stat) => stat.name === "Formulários")?.count ||
            "0";
          return sum + parseInt(forms);
        }, 0)
      ),
      totalTechnicalPieces: formatTwoDigits(
        productsExceptGeral.reduce((sum, card) => {
          const pieces =
            card.stats.find((stat) => stat.name === "Peças Técnicas")?.count ||
            "0";
          return sum + parseInt(pieces);
        }, 0)
      ),
      // totalAgendas: formatTwoDigits(productsExceptGeral.reduce((sum, card) => {
      //   const agendas = card.stats.find(stat => stat.name === "Agendas")?.count || "0";
      //   return sum + parseInt(agendas);
      // }, 0)),
      totalAgendas: totalAppointments,
      // totalCollaborators: formatTwoDigits(productsExceptGeral.reduce((sum, card) => {
      //   const collaborators = card.stats.find(stat => stat.name === "Colaboradores")?.count || "0";
      //   return sum + parseInt(collaborators);
      // }, 0)),
      totalCollaborators: totalUsers,
    };
  }, [productCards, formatTwoDigits, totalAppointments, totalUsers]);

  // Atualiza os totais do card "Geral"
  const updatedGeneralCard = useMemo(() => {
    const totals = calculateTotals();
    console.log(totals);
    // Cria uma cópia do card Geral com os totais atualizados
    const generalCard = { ...initialGeneralCard };
    generalCard.stats = [
      { name: "Produtos", count: totals.totalProducts, icon: "/-cone-30.svg" },
      { name: "Fases", count: totals.totalPhases, icon: "/-cone.svg" },
      {
        name: "Atividades",
        count: totals.totalActivities,
        icon: "/-cone-1.svg",
      },
      { name: "Formulários", count: totals.totalForms, icon: "/-cone-4.svg" },
      {
        name: "Peças Técnicas",
        count: totals.totalTechnicalPieces,
        icon: "/-cone-4.svg",
      },
      { name: "Agendas", count: totals.totalAgendas, icon: "/-cone-4.svg" },
      {
        name: "Colaboradores",
        count: totals.totalCollaborators,
        icon: "/-cone-4.svg",
      },
    ];

    return generalCard;
  }, [calculateTotals]);

  // Atualiza o array de produtos para refletir os totais atualizados no card "Geral"
  const finalProductCards = useMemo(() => {
    const filteredCards = productCards.filter((card) => card.id !== "geral");
    return [updatedGeneralCard, ...filteredCards];
  }, [updatedGeneralCard, productCards]);

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    return finalProductCards.filter((product) =>
      product.title.toLowerCase().includes(filter.toLowerCase())
    );
  }, [finalProductCards, filter]);

  const handleCardSelection = (card: any) => {
    if (selectedCardId === card.id) {
      setSelectedCardId("");
      setSelectedProduct("Produtos");
    } else {
      setSelectedCardId(card.id);
      setSelectedProduct(card.title);
    }
    // Redefinir o filtro de fase para "todas" quando mudar de produto
    setSelectedPhaseFilter("");
    setSelectedPhase("Fase");
    setSelectedCardActivityId("");
    setSelectedActivity("Atividade");
  };

  // Get the currently selected card's data
  const selectedCard = useMemo(() => {
    return finalProductCards.find((card) => card.id === selectedCardId);
  }, [selectedCardId, finalProductCards]);

  // Get the subtitle text based on selected card
  const subtitleText = useMemo(() => {
    return selectedCard?.title || "Geral";
  }, [selectedCard]);

  // Generate navigation tabs based on selected card
  const navTabs = useMemo(() => {
    const baseNames = [
      "Produtos",
      "Fases",
      "Atividades",
      "Formulários",
      "Peças Técnicas",
      "Agendas",
      "Colaboradores",
    ];

    return baseNames.map((name) => {
      let count = "";

      // Only show counts if we're on the Geral card
      if (selectedCardId === "geral") {
        console.log(selectedCard, name);

        if (name === "Produtos") {
          const generalCard = finalProductCards.find(
            (card) => card.id === "geral"
          );
          const productStat = generalCard?.stats.find(
            (s) => s.name === "Produtos"
          );
          count = productStat?.count || "00";
        } else {
          const stat = selectedCard?.stats.find((s) => s.name === name);
          count = stat?.count || "00";
        }
      } else if (name !== "Produtos") {
        // For other cards, show individual counts except for "Produtos"

        const stat = selectedCard?.stats.find((s) => s.name === name);
        count = stat?.count || "00";
      }

      return {
        name,
        count,
      };
    });
  }, [selectedCardId, selectedCard, finalProductCards]);

  const handleScroll = (direction: "left" | "right") => {
    const currentIndex = navTabs.findIndex((tab) => tab.name === selectedTab);
    let newIndex;

    if (direction === "right") {
      newIndex =
        currentIndex < navTabs.length - 1 ? currentIndex + 1 : currentIndex;
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
    }

    const newTab = navTabs[newIndex];
    setSelectedTab(newTab.name);

    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const tabs = container.querySelectorAll('[role="tab"]');
      const targetTab = tabs[newIndex] as HTMLElement;

      if (targetTab) {
        const containerRect = container.getBoundingClientRect();
        const tabRect = targetTab.getBoundingClientRect();
        const scrollLeft =
          container.scrollLeft +
          (tabRect.left - containerRect.left) -
          (containerRect.width - tabRect.width) / 2;

        container.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    }
  }; // Função para abrir o modal de confirmação de exclusão
  const handleEditClick = useCallback((id: string) => {
    navigate(`/products/edit/${id}`);
  }, []);
  // Função para abrir o modal de confirmação de exclusão
  const handleDeleteClick = useCallback((id: string, name: string) => {
    setDeleteId(id);
    setDeleteItemName(name);
  }, []);

  // Função para cancelar a exclusão
  const handleCancelDelete = useCallback(() => {
    setDeleteId(null);
    setDeleteItemName("");
  }, []);

  // Função para confirmar a exclusão
  const handleConfirmDelete = useCallback(async () => {
    if (deleteId) {
      try {
        console.log("🗑️ Iniciando exclusão do produto:", deleteId);
        await dispatch(deleteProduct(deleteId));
        console.log("✅ Produto excluído com sucesso");

        // Atualizar a lista após exclusão
        loadData();
      } catch (error) {
        console.error("❌ Erro ao excluir produto:", error);
      } finally {
        // Fechar o modal independentemente do resultado
        setDeleteId(null);
        setDeleteItemName("");
      }
    }
  }, [deleteId, dispatch, loadData]);

  // Estado para controlar as fases de todos os produtos

  // Efeito para extrair as fases de todos os produtos quando os dados forem carregados
  useEffect(() => {
    if (items && items.length > 0) {
      const extractedPhases: PhaseData[] = [];
      const extractedActivities: ActivityData[] = [];

      items.forEach((product) => {
        if (product.phases && product.phases.length > 0) {
          product.phases.forEach((phase) => {
            // Calcular a duração total da fase
            const phaseDuration =
              phase.activities?.reduce(
                (sum, activity) => sum + (activity.duration || 0),
                0
              ) || 0;

            // Calcular o valor estimado
            const estimatedValue = phaseDuration * 10;

            const phaseId = phase._id;

            extractedPhases.push({
              id: phaseId,
              title: phase.name,
              image: phase.image ? phase.image : "/fase.svg",

              description: phase.description || "Sem descrição...",
              hours: `${phaseDuration}h`,
              value: `R$ ${estimatedValue.toFixed(2).replace(".", ",")}`,
              icon: "/-cone.svg",
              // Adicionar o ID do produto para permitir filtrar por produto
              productId: product._id || product.key,
            });

            // Extrair atividades desta fase
            if (phase.activities && phase.activities.length > 0) {
              phase.activities.forEach((activity) => {
                const activityDuration = activity.duration || 0;
                const activityValue = activityDuration * 10;

                extractedActivities.push({
                  id: activity._id || `${phaseId}-${activity.name}`,
                  title: activity.name,
                  image: phase.image ? phase.image : "/atividade.svg",

                  description: activity.description || "Sem descrição...",
                  duration: activityDuration,
                  hours: `${activityDuration}h`,
                  value: `R$ ${activityValue.toFixed(2).replace(".", ",")}`,
                  icon: "/-cone-1.svg",
                  productId: product._id || product.key,
                  phaseId: phaseId,
                });
              });
            }
          });
        }
      });
      console.log("fases", extractedPhases);

      setAllPhases(extractedPhases);
      setAllActivities(extractedActivities);
    }
  }, [items]);

  // Filtrar fases com base na pesquisa e no produto selecionado
  const filteredPhases = useMemo(() => {
    return allPhases.filter((phase) => {
      // Filtrar por texto de pesquisa
      const title = phase.title.toLowerCase();
      const description = phase.description
        ? phase.description.toLowerCase()
        : "";
      const searchTerm = phaseFilter.toLowerCase();
      const matchesSearch =
        title.includes(searchTerm) || description.includes(searchTerm);

      // Filtrar por produto selecionado
      // Se o card selecionado for "geral", mostrar todas as fases
      // Caso contrário, mostrar apenas as fases do produto selecionado
      const matchesProduct =
        selectedCardId === "geral" || phase.productId === selectedCardId;

      return matchesSearch && matchesProduct;
    });
  }, [allPhases, phaseFilter, selectedCardId]);

  // Filtrar atividades com base na pesquisa e no produto selecionado
  const filteredActivities = useMemo(() => {
    return allActivities.filter((activity) => {
      // Filtrar por texto de pesquisa
      const title = activity.title.toLowerCase();
      const description = activity.description
        ? activity.description.toLowerCase()
        : "";
      const searchTerm = activityFilter.toLowerCase();
      const matchesSearch =
        title.includes(searchTerm) || description.includes(searchTerm);

      // Filtrar por produto selecionado
      // Se o card selecionado for "geral", mostrar todas as atividades
      // Caso contrário, mostrar apenas as atividades do produto selecionado
      const matchesProduct =
        selectedCardId === "geral" || activity.productId === selectedCardId;

      // Filtrar por fase selecionada (se houver)
      // Se a fase selecionada for "todas", mostrar todas as atividades
      // Caso contrário, mostrar apenas as atividades da fase selecionada
      const matchesPhase =
        selectedPhaseFilter === "todas" ||
        activity.phaseId === selectedPhaseFilter;

      return matchesSearch && matchesProduct && matchesPhase;
    });
  }, [allActivities, activityFilter, selectedCardId, selectedPhaseFilter]);

  // Funções para manipular as fases
  const handleViewPhase = useCallback((id: string) => {
    console.log("Visualizando fase:", id);
    // Implementar a lógica para visualizar a fase
  }, []);

  const handleEditPhase = useCallback((id: string) => {
    console.log("Editando fase:", id);
    // Implementar a lógica para editar a fase
  }, []);

  const handleDeletePhase = useCallback((id: string) => {
    console.log("Excluindo fase:", id);
    // Lógica para excluir a fase - necessário implementar de acordo com sua API
  }, []);

  // Funções para manipular as atividades
  const handleViewActivity = useCallback((id: string) => {
    console.log("Visualizando atividade:", id);
    // Implementar a lógica para visualizar a atividade
  }, []);

  const handleEditActivity = useCallback((id: string) => {
    console.log("Editando atividade:", id);
    // Implementar a lógica para editar a atividade
  }, []);

  const handleDeleteActivity = useCallback((id: string) => {
    console.log("Excluindo atividade:", id);
    // Lógica para excluir a atividade - necessário implementar de acordo com sua API
  }, []);

  // Filtrar as fases disponíveis com base no produto selecionado
  const availablePhases = useMemo(() => {
    // Se não houver produto selecionado, mostrar todas as fases
    if (selectedCardId === "geral") {
      return allPhases;
    }

    // Caso contrário, filtrar as fases do produto selecionado
    return allPhases.filter((phase) => phase.productId === selectedCardId);
  }, [allPhases, selectedCardId]);

  // Componente para a aba Fases
  const PhasesTabContent = useCallback(() => {
    console.log(filteredPhases);
    return (
      <div className="flex flex-col gap-4 w-full">
        {/* Barra de pesquisa para fases */}

        {/* Lista de fases */}
        {loading ? (
          <div className="p-[25px]">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-[84px] bg-muted/40 rounded-[14px]"
                ></div>
              ))}
            </div>
          </div>
        ) : filteredPhases.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-6">
            <p className="text-clarotextos-03 mb-4">
              {selectedCardId === "geral"
                ? "Nenhuma fase encontrada"
                : `Nenhuma fase encontrada para ${selectedCard?.title || ""}`}
            </p>
            <Button
              variant="outline"
              onClick={() => console.log("Adicionar primeira fase")}
              className="border-clarotextos-03 text-clarotextos-02"
            >
              {selectedCardId === "geral"
                ? "Adicionar Primeira Fase"
                : `Adicionar Fase para ${selectedCard?.title || ""}`}
            </Button>
          </div>
        ) : (
          <DataTable
            selectedRow={selectedPhaseFilter}
            onClickRow={(row: any) => {
              console.log(row);
              if (selectedPhaseFilter === row.id) {
                setSelectedPhaseFilter("");
                setSelectedPhase("Fase");
              } else {
                setSelectedPhaseFilter(row.id);
                setSelectedPhase(row.title);
              }
            }}
            columns={[
              {
                image: "image",
                style_image: {
                  borderRadius: 10,
                  border: "1px solid lightgrey",
                  background: "rgb(244, 244, 244)",
                  width: 30,
                  height: 30,
                  objectFit: "contain",
                },
                key: "name",
                header: "Fase",
                render: (value: any) => String(value || ""),
              },
              // {
              //   key: 'email' as keyof UserOutputDTO,
              //   header: 'Email',
              //   render: (value: any) => String(value || ''),
              // },
              {
                key: "description",
                header: "Descrição",
                render: (value: any) => (value ? String(value || "") : "-"),
              },
              {
                key: "hours",
                header: "Tempo (h)",
                render: (value: any) => (value ? String(value || "") : "-"),
              },
              {
                key: "value",
                header: "Valor (R$)",
                render: (value: any) => (value ? String(value || "") : "-"),
              },

              // {
              //   key: 'createdAt' as keyof UserOutputDTO,
              //   header: 'Data de Criação',
              //   render: (value: any) => value ? new Date(value as Date).toLocaleDateString() : '-',
              // },
            ]}
            hide_header
            data={filteredPhases.map((item) => ({ ...item, name: item.title }))}
            title={"Colaboradores"}
            subtitle={"Lista de colaboradores cadatrados no sistema"}
            basePath="products"
            onDelete={() => {}}
            loading={loading}
            hide_actions
            error={error}
            newFormat
          />
        )}
      </div>
    );
  }, [
    filteredPhases,
    loading,
    phaseFilter,
    handleViewPhase,
    handleEditPhase,
    handleDeletePhase,
    selectedCardId,
    selectedCard,
  ]);

  // const [selectedPhase,setSelectedPhase] = useState('');
  // Componente para a aba Atividades
  const ActivitiesTabContent = useCallback(() => {
    console.log("todas fases", allPhases, allActivities);
    return (
      <div className="flex flex-col gap-4 w-full">
        {/* Filtro de fases */}
        {/* <div className="w-full bg-clarofrente-01 rounded-xl p-4 shadow-sombra-01">
          <div className="flex flex-col gap-3">
            
            <div className="w-full">
              <Select 
                value={selectedPhaseFilter} 
                onValueChange={setSelectedPhaseFilter}
              >
                <SelectTrigger className="w-full h-11 border border-clarotextos-03 bg-white text-clarotextos-02 rounded-xl focus:ring-1 focus:ring-claroprimaria-01 focus:ring-offset-0">
                  <SelectValue placeholder="Selecione uma fase" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-clarotextos-03 rounded-lg">
                  <SelectItem value="todas" className="text-clarotextos-02 focus:bg-claroprimaria-01/10 hover:bg-claroprimaria-01/10 focus:text-clarotextos-01">Todas as fases</SelectItem>
                  {availablePhases.map(phase => (
                    <SelectItem 
                      key={phase.id} 
                      value={phase.id} 
                      className="text-clarotextos-02 focus:bg-claroprimaria-01/10 hover:bg-claroprimaria-01/10 focus:text-clarotextos-01"
                    >
                      <span className="truncate">{phase.title}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div> */}

        {/* Barra de pesquisa para atividades */}
        {/* <div className="flex items-start gap-5 w-full">
          <div className="flex h-11 items-center gap-2.5 p-2.5 flex-1 bg-clarofrente-01 rounded-xl shadow-sombra-01">
            <div className="flex w-full items-center gap-2 bg-clarofrente-01">
              <div className="relative w-6 h-6">
                <SearchIcon className="w-[19px] h-[19px] absolute top-[3px] left-[3px] text-clarotextos-03" />
              </div>
              <Input
                className="border-0 shadow-none bg-transparent font-semantic-input font-[number:var(--semantic-input-font-weight)] text-clarotextos-03 text-[length:var(--semantic-input-font-size)] tracking-[var(--semantic-input-letter-spacing)] leading-[var(--semantic-input-line-height)] [font-style:var(--semantic-input-font-style)] focus-visible:ring-0 focus-visible:ring-offset-0 p-0"
                placeholder={selectedCardId === "geral" ? "Pesquisar atividades..." : `Pesquisar atividades de ${selectedCard?.title || ""}...`}
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value)}
              />
            </div>
          </div>

          <Button className="h-11 px-5 py-[15px] bg-claroprimaria-01 rounded-xl">
            <img
              className="mt-[-2.50px] mb-[-2.50px]"
              alt="Element"
              src="/03-5.svg"
            />
            <span className="ml-2 font-inter-16-semi-bold font-[number:var(--inter-16-semi-bold-font-weight)] text-clarobg-202 text-[length:var(--inter-16-semi-bold-font-size)] tracking-[var(--inter-16-semi-bold-letter-spacing)] leading-[var(--inter-16-semi-bold-line-height)] whitespace-nowrap [font-style:var(--inter-16-semi-bold-font-style)]">
              Nova Atividade
            </span>
          </Button>
        </div> */}

        <DataTable
          selectedRow={selectedCardActivityId}
          onClickRow={(row: any) => {
            if (selectedCardActivityId === row.id) {
              setSelectedCardActivityId("");
              setSelectedActivity("Atividade");
            } else {
              setSelectedCardActivityId(row.id);
              setSelectedActivity(row.title);
            }
          }}
          columns={[
            {
              image: "image",
              style_image: {
                borderRadius: 10,
                border: "1px solid lightgrey",
                background: "rgb(244, 244, 244)",
                width: 30,
                height: 30,
                objectFit: "contain",
              },
              key: "name",
              header: "Fase",
              render: (value: any) => String(value || ""),
            },
            // {
            //   key: 'email' as keyof UserOutputDTO,
            //   header: 'Email',
            //   render: (value: any) => String(value || ''),
            // },
            {
              key: "description",
              header: "Descrição",
              render: (value: any) => (value ? String(value || "") : "-"),
            },
            {
              key: "hours",
              header: "Tempo (h)",
              render: (value: any) => (value ? String(value || "") : "-"),
            },
            {
              key: "value",
              header: "Valor (R$)",
              render: (value: any) => (value ? String(value || "") : "-"),
            },

            // {
            //   key: 'createdAt' as keyof UserOutputDTO,
            //   header: 'Data de Criação',
            //   render: (value: any) => value ? new Date(value as Date).toLocaleDateString() : '-',
            // },
          ]}
          children={() => (
            <div
              className="mb-4 flex w-full"
              style={{
                position: "relative",
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              <div style={{ width: "100%" }}>
                <label className="text-sm font-semibold text-gray-700">
                  Fase (
                  {
                    allPhases?.filter(
                      (item: any) =>
                        item.productId == selectedCardId ||
                        selectedCardId == "geral"
                    ).length
                  }
                  )
                </label>
                <select
                  name="phase_select"
                  value={selectedPhaseFilter}
                  onChange={(e) => {
                    console.log(e.target.value);
                    setSelectedPhaseFilter(e.target.value);
                  }}
                  required
                  style={{ fontSize: "0.875rem", padding: 5, borderRadius: 20 }}
                  className={`mt-1 h-12 w-full border rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 `}
                >
                  <option value={"todas"}>Todas as fases</option>
                  {/* <option value={'Geral'}>
                  Geral
                </option>
                <option value={'Comercial'}>
                  Comercial
                </option> */}

                  {allPhases
                    .filter(
                      (item: any) =>
                        item.productId == selectedCardId ||
                        selectedCardId == "geral"
                    )
                    ?.map((product: any) => (
                      <option key={product.id} value={product.id}>
                        {product.title}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          )}
          hide_header
          data={filteredActivities.map((item) => ({
            ...item,
            name: item.title,
          }))}
          title={"Colaboradores"}
          subtitle={"Lista de colaboradores cadatrados no sistema"}
          basePath="products"
          onDelete={() => {}}
          loading={loading}
          hide_actions
          error={error}
          newFormat
        />

        {/* Lista de atividades */}
        {/* <Card style={{ maxHeight: (window.innerHeight-280), overflow: 'scroll' }} className="mt-2 w-full border border-[#D8E0F0] rounded-[14px] bg-[#FCFCFC] hover:border-[#c8d0e0] transition-colors">
          <CardContent className="flex flex-col  p-0 relative">
            <ScrollArea className="w-full h-full">
              {loading ? (
                <div className="p-[25px]">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-[84px] bg-muted/40 rounded-[14px]"></div>
                    ))}
                  </div>
                </div>
              ) : filteredActivities.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-6">
                  <p className="text-clarotextos-03 mb-4">
                    {selectedCardId === "geral" 
                      ? selectedPhaseFilter === "todas"
                        ? "Nenhuma atividade encontrada" 
                        : `Nenhuma atividade encontrada para a fase selecionada`
                      : selectedPhaseFilter === "todas"
                        ? `Nenhuma atividade encontrada para ${selectedCard?.title || ""}`
                        : `Nenhuma atividade encontrada para a fase selecionada em ${selectedCard?.title || ""}`
                    }
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => console.log("Adicionar primeira atividade")}
                    className="border-clarotextos-03 text-clarotextos-02"
                  >
                    {selectedCardId === "geral" 
                      ? "Adicionar Primeira Atividade" 
                      : `Adicionar Atividade para ${selectedCard?.title || ""}`}
                  </Button>
                </div>
              ) : (
                <div className="p-[25px] space-y-4">
                  {filteredActivities.map(activity => (
                    <Element1ByAnima
                      key={activity.id}
                      phase={{
                        id: activity.id,
                        title: activity.title,
                        description: activity.description,
                        hours: activity.hours,
                        value: activity.value,
                        icon: activity.icon,
                        productId: activity.productId
                      }}
                      onView={handleViewActivity}
                      onEdit={handleEditActivity}
                      onDelete={handleDeleteActivity}
                    />
                  ))}
                </div>
              )}
              <ScrollBar orientation="vertical" />
            </ScrollArea>
          </CardContent>
        </Card> */}
      </div>
    );
  }, [
    filteredActivities,
    loading,
    activityFilter,
    handleViewActivity,
    handleEditActivity,
    handleDeleteActivity,
    selectedCardId,
    selectedCard,
    availablePhases,
    selectedPhaseFilter,
  ]);

  // Function to find the first form activity in a product
  const findFirstFormActivity = useCallback((product: IProduct) => {
    if (!product.phases) return null;

    for (const phase of product.phases) {
      if (!phase.activities) continue;

      const formActivity = phase.activities.find(
        (activity) => activity.type === ActivityType.FORM && activity.typeRefId
      );

      if (formActivity) {
        return {
          phaseId: phase._id || "",
          formId: formActivity.typeRefId,
        };
      }
    }

    return null;
  }, []);

  // Function to handle link generation
  const handleGenerateLink = useCallback(
    async (product: IProduct) => {
      const formActivity = findFirstFormActivity(product);
      if (!formActivity || !formActivity.formId) return;

      try {
        // Consultar a API para obter o formulário
        const resultAction = await dispatch(
          fetchPhaseFormById(formActivity.formId)
        );

        if (fetchPhaseFormById.fulfilled.match(resultAction)) {
          const phaseForm = resultAction.payload;

          // Verificar se o formulário está aberto
          if (phaseForm.type === PhaseFormType.OPENED) {
            const processId = "new";
            const productId = product._id || "";
            const formUrl = `${window.location.origin}/forms/${formActivity.formId}/${processId}/${productId}`;

            // Copy to clipboard
            navigator.clipboard.writeText(formUrl).then(() => {
              console.log("Link copiado para a área de transferência!");
              setLinkCopied(product._id || "");

              // Reset visual feedback after 2 seconds
              setTimeout(() => setLinkCopied(null), 2000);
            });
          } else {
            console.log("Formulário está fechado, abrindo modal de WhatsApp");
            // Abrir modal de WhatsApp para formulários fechados
            setCurrentFormId(formActivity.formId);
            setCurrentProductId(product._id || "");
            setPhoneModalOpen(true);
          }
        } else {
          console.error("Erro ao buscar formulário");
        }
      } catch (error) {
        console.error("Erro ao verificar status do formulário:", error);
      }
    },
    [findFirstFormActivity, dispatch]
  );

  // Função para enviar WhatsApp
  const handlePhoneSubmit = useCallback(async () => {
    if (!currentFormId || !currentProductId) return;

    // Process phone numbers and add +55 if country code is missing
    const phones = phoneInput.split(",").map((phone) => {
      const trimmedPhone = phone.trim();
      // If phone doesn't start with +, add +55
      return trimmedPhone.startsWith("+") ? trimmedPhone : `+55${trimmedPhone}`;
    });

    const payload = {
      phones,
      phaseFormId: currentFormId,
      processId: "new",
      productId: currentProductId,
    };

    try {
      await filledFormService.submitPhones(payload);
      console.log("Phone numbers submitted successfully");
      notification.showSuccess("Números de WhatsApp enviados com sucesso!");
      setPhoneModalOpen(false);
      setPhoneInput("");
      setCurrentFormId(null);
      setCurrentProductId(null);
    } catch (error) {
      console.error("Error submitting phone numbers:", error);
      notification.showError(
        "Erro ao enviar números de WhatsApp. Tente novamente."
      );
    }
  }, [currentFormId, currentProductId, phoneInput, notification]);

  // Função para fechar o modal e limpar estados
  const handleClosePhoneModal = useCallback(() => {
    setPhoneModalOpen(false);
    setPhoneInput("");
    setCurrentFormId(null);
    setCurrentProductId(null);
  }, []);

  // Conteúdo baseado na tab selecionada
  const renderTabContent = useCallback(() => {
    return (
      <div className="w-full">
        <div
          style={{ display: selectedTab == "Fases" ? "block" : "none" }}
          className="w-full"
        >
          <PhasesTabContent />
        </div>
        <div
          style={{ display: selectedTab == "Atividades" ? "block" : "none" }}
          className="w-full"
        >
          <ActivitiesTabContent />
        </div>
        <div
          style={{ display: selectedTab == "Agendas" ? "block" : "none" }}
          className="w-full"
        >
          <AppointmentList
            setTotalAppointments={(value) => {
              setTotalAppointments(value);
            }}
            hide_header
          />
        </div>
        <div
          style={{ display: selectedTab == "Colaboradores" ? "block" : "none" }}
          className="w-full"
        >
          <UserList
            setTotal={(value) => {
              setTotalUsers(value);
            }}
            hide_header
          />
        </div>
        <div
          style={{ display: selectedTab == "Produtos" ? "block" : "none" }}
          className="w-full"
        >
          <>
            {/* Search and Add Product */}
            <div className="flex items-start gap-5 w-full">
              <div className="flex h-11 items-center gap-2.5 p-2.5 flex-1 bg-clarofrente-01 rounded-xl shadow-sombra-01 px-0">
                <div
                  className="flex w-full items-center gap-2 bg-clarofrente-01"
                  style={{ position: "relative" }}
                >
                  <input
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Pesquisar"
                    className="p-2 border rounded"
                    style={{
                      width: "-webkit-fill-available",
                      marginRight: 20,
                      borderRadius: 20,
                      paddingLeft: 40,
                    }}
                  />
                  <Search
                    style={{
                      position: "absolute",
                      top: "20%",
                      left: 10,
                      color: "grey",
                    }}
                  />
                  <Link
                    to={`/products/new`}
                    style={{ width: 160, borderRadius: 20 }}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  >
                    <p style={{ whiteSpace: "nowrap" }}>
                      {"Adicionar"} <AddIcon />
                    </p>
                  </Link>

                  {/* <div className="relative w-6 h-6">
                    <SearchIcon className="w-[19px] h-[19px] absolute top-[3px] left-[3px] text-clarotextos-03" />
                  </div>
                  <Input
                    className="border rounded bg-transparent font-semantic-input font-[number:var(--semantic-input-font-weight)] text-clarotextos-03 text-[length:var(--semantic-input-font-size)] tracking-[var(--semantic-input-letter-spacing)] leading-[var(--semantic-input-line-height)] [font-style:var(--semantic-input-font-style)] focus-visible:ring-0 focus-visible:ring-offset-0 p-0"
                    placeholder="Pesquise o que você precisa aqui..."
                    value={filter}
                    onChange={(e) => handleFilter({"name": e.target.value})}
                  /> */}
                </div>
              </div>

              {/* <Button onClick={handleNewClick} className="h-11 px-5 py-[15px] bg-claroprimaria-01 rounded-xl">
                <img
                  className="mt-[-2.50px] mb-[-2.50px]"
                  alt="Element"
                  src="/03-5.svg"
                />
                <span className="ml-2 font-inter-16-semi-bold font-[number:var(--inter-16-semi-bold-font-weight)] text-clarobg-202 text-[length:var(--inter-16-semi-bold-font-size)] tracking-[var(--inter-16-semi-bold-letter-spacing)] leading-[var(--inter-16-semi-bold-line-height)] whitespace-nowrap [font-style:var(--inter-16-semi-bold-font-style)]">
                  Produto
                </span>
              </Button> */}
            </div>

            {/* Product Cards */}
            <Card
              style={{
                maxHeight: window.innerHeight - 280,
                overflow: "scroll",
              }}
              className="custom-scrollbar mt-2 w-full border border-[#D8E0F0] rounded-[14px] bg-[#FCFCFC] hover:border-[#c8d0e0] transition-colors"
            >
              <CardContent className="flex flex-col p-0 relative">
                <ScrollArea className="w-full h-full">
                  {loading && !isInitialLoad ? (
                    <ProductsGridSkeleton />
                  ) : (
                    <div
                      className="grid auto-rows-[364px] gap-[20px] p-[25px]"
                      style={{
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(256px, 1fr))",
                        gridAutoFlow: "dense",
                      }}
                    >
                      {filteredProducts.map((card, index) => (
                        <Card
                          key={card.id}
                          className={`flex flex-col h-full rounded-xl overflow-hidden border border-solid cursor-pointer ${
                            selectedCardId === card.id
                              ? "border-[#1776cf]"
                              : "border-[#d8e0f0]"
                          }`}
                          onClick={() => handleCardSelection(card)}
                        >
                          <CardHeader
                            className={`h-[46px] flex w-full items-center justify-center px-0 py-3.5 bg-clarofrente-01 rounded-[12px_12px_0px_0px] border-b-[0.8px] [border-bottom-style:solid] ${
                              selectedCardId === card.id
                                ? "border-[#1776cf]"
                                : "border-[#d8e0f0]"
                            } p-0`}
                          >
                            {card.id === "geral" ? (
                              <img
                                className="flex-1 mt-[-14.00px] mb-[-14.00px]"
                                alt="Cone"
                                src="/-cone-23.svg"
                              />
                            ) : (
                              <div className="flex w-full items-center justify-between px-2.5">
                                <div
                                  style={{ color: "grey" }}
                                  className="font-inter-16-medium font-[number:var(--inter-16-medium-font-weight)] text-clarotextos-02 text-[length:var(--inter-16-medium-font-size)] whitespace-nowrap tracking-[var(--inter-16-medium-letter-spacing)] leading-[var(--inter-16-medium-line-height)] [font-style:var(--inter-16-medium-font-style)]"
                                >
                                  {card.number_product
                                    ? card.number_product
                                    : String(index).padStart(2, "0")}
                                </div>
                                <div className="inline-flex h-[18px] items-center justify-center gap-5">
                                  {card.id !== "geral" &&
                                    items.find((p) => p._id === card.id) &&
                                    findFirstFormActivity(
                                      items.find((p) => p._id === card.id)!
                                    ) && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <svg
                                              className={`cursor-pointer ${
                                                linkCopied === card.id
                                                  ? "opacity-50"
                                                  : ""
                                              } w-[18px] h-[18px]`}
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                              onClick={(e) => {
                                                e.stopPropagation(); // Evita acionar o onClick do Card
                                                console.log("clicou");
                                                const product = items.find(
                                                  (p) => p._id === card.id
                                                );
                                                if (product)
                                                  handleGenerateLink(product);
                                              }}
                                            >
                                              <path
                                                d="M10 13C10.4295 13.5741 10.9774 14.0491 11.6066 14.3929C12.2358 14.7367 12.9315 14.9411 13.6467 14.9913C14.3618 15.0415 15.0796 14.9364 15.7517 14.6828C16.4238 14.4291 17.0338 14.0329 17.54 13.52L20.54 10.52C21.4508 9.59695 21.9548 8.33397 21.9436 7.02299C21.9324 5.71201 21.4068 4.45794 20.479 3.53004C19.5511 2.60214 18.297 2.07659 16.986 2.06537C15.675 2.05415 14.4121 2.55821 13.489 3.46904L11.639 5.31004"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                              <path
                                                d="M14 11C13.5705 10.4259 13.0226 9.95085 12.3934 9.60705C11.7642 9.26325 11.0685 9.05888 10.3534 9.00868C9.63821 8.95849 8.92041 9.06363 8.24826 9.31723C7.57612 9.57082 6.96609 9.96703 6.46 10.48L3.46 13.48C2.54917 14.403 2.04511 15.666 2.05633 16.977C2.06755 18.288 2.5931 19.5421 3.521 20.47C4.4489 21.3979 5.70297 21.9234 7.01395 21.9346C8.32493 21.9458 9.58791 21.4418 10.511 20.531L12.361 18.69"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>Copiar link do formulário</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  <img
                                    className="cursor-pointer"
                                    alt="Edit"
                                    src="/01.svg"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Evita acionar o onClick do Card
                                      handleEditClick(card.id);
                                    }}
                                  />
                                  {/* <img
                                    className=""
                                    alt="Element"
                                    src="/02-1.svg"
                                  /> */}
                                  <img
                                    className="cursor-pointer"
                                    alt="Delete"
                                    src="/03.svg"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Evita acionar o onClick do Card
                                      handleDeleteClick(card.id, card.title);
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </CardHeader>

                          <CardContent className="p-0 h-[318px] flex flex-col">
                            <div className="flex items-center gap-2.5 p-2.5">
                              <div className="flex items-center justify-center gap-2.5 flex-1">
                                <div
                                  style={{ color: "grey" }}
                                  className="font-inter-14-regular font-[number:var(--inter-14-regular-font-weight)] text-clarotextos-01 text-[length:var(--inter-14-regular-font-size)] tracking-[var(--inter-14-regular-letter-spacing)] leading-[var(--inter-14-regular-line-height)] [font-style:var(--inter-14-regular-font-style)]"
                                >
                                  {card.title}
                                </div>
                              </div>
                            </div>

                            <div className="px-3">
                              <div
                                className="w-full h-[127px] rounded-md bg-cover bg-center"
                                style={{
                                  backgroundImage: `url(${card.image})`,
                                }}
                              />
                            </div>

                            <ScrollArea className="flex-1 w-full" type="scroll">
                              <div className="flex flex-col pt-0 pb-[5px] px-[15px] rounded-[0px_0px_8px_8px]">
                                {card.id !== "geral" && (
                                  <>
                                    <div className="flex items-center gap-2.5 px-0 py-2.5 w-full hover:bg-[#f4f4f4] rounded-[6px] transition-colors duration-200 cursor-pointer">
                                      <img
                                        className="w-3.5 h-3.5"
                                        alt="Clock"
                                        src="/-cone-2.svg"
                                      />
                                      <div className="flex items-center gap-2.5 flex-1">
                                        <div
                                          style={{ color: "grey" }}
                                          className="font-inter-14-regular text-clarotextos-01"
                                        >
                                          {card.hours}
                                        </div>
                                      </div>
                                      <div
                                        style={{ color: "grey" }}
                                        className="font-inter-14-regular text-clarotextos-01"
                                      >
                                        {card.value}
                                      </div>
                                    </div>
                                    <div className="w-full">
                                      <img
                                        className="h-px w-full"
                                        alt="Linha"
                                        src="/linha.svg"
                                      />
                                    </div>
                                  </>
                                )}
                                {card.stats.map((stat) => (
                                  <div
                                    key={`${card.id}-${stat.name}`}
                                    className="flex items-center gap-2.5 px-0 py-2.5 w-full hover:bg-[#f4f4f4] rounded-[6px] transition-colors duration-200 cursor-pointer"
                                  >
                                    <img
                                      className={`${
                                        stat.icon.includes("cone-30")
                                          ? "w-[17.2px] h-[14.2px] ml-[-1.60px]"
                                          : stat.icon.includes("cone.svg")
                                          ? "w-[16.3px] h-[14.3px] ml-[-1.15px]"
                                          : stat.icon.includes("cone-4")
                                          ? "w-3.5 h-[14.3px]"
                                          : ""
                                      }`}
                                      alt="Icon"
                                      src={stat.icon}
                                    />
                                    <div className="flex items-center gap-2.5 flex-1">
                                      <div
                                        style={{ color: "grey" }}
                                        className="font-inter-14-regular text-clarotextos-01"
                                      >
                                        {stat.name}
                                      </div>
                                    </div>
                                    <div
                                      style={{ color: "grey" }}
                                      className="font-inter-14-regular text-clarotextos-01"
                                    >
                                      {stat.count}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              <ScrollBar
                                orientation="vertical"
                                className="bg-clarobarra-de-rolagem02"
                              />
                            </ScrollArea>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                  <ScrollBar orientation="vertical" />
                </ScrollArea>
              </CardContent>
            </Card>
          </>
        </div>
      </div>
    );

    switch (selectedTab) {
      case "Fases":
        return <PhasesTabContent />;
      case "Atividades":
        return <ActivitiesTabContent />;
      case "Agendas":
        return (
          <AppointmentList setCountAppointments={(value) => {}} hide_header />
        );
      case "Produtos":
      default:
        return (
          <>
            {/* Search and Add Product */}
            <div className="flex items-start gap-5 w-full">
              <div className="flex h-11 items-center gap-2.5 p-2.5 flex-1 bg-clarofrente-01 rounded-xl shadow-sombra-01">
                <div
                  className="flex w-full items-center gap-2 bg-clarofrente-01"
                  style={{ position: "relative" }}
                >
                  <input
                    type="text"
                    value={filter}
                    onChange={(e) => handleFilter({ name: e.target.value })}
                    placeholder="Pesquisar"
                    className="p-2 border rounded"
                    style={{
                      width: "-webkit-fill-available",
                      marginRight: 20,
                      borderRadius: 20,
                      paddingLeft: 40,
                    }}
                  />
                  <Search
                    style={{
                      position: "absolute",
                      top: "20%",
                      left: 10,
                      color: "grey",
                    }}
                  />
                  <Link
                    to={`/products/new`}
                    style={{ width: 160, borderRadius: 20 }}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  >
                    <p style={{ whiteSpace: "nowrap" }}>
                      {"Adicionar"} <AddIcon />
                    </p>
                  </Link>

                  {/* <div className="relative w-6 h-6">
                    <SearchIcon className="w-[19px] h-[19px] absolute top-[3px] left-[3px] text-clarotextos-03" />
                  </div>
                  <Input
                    className="border rounded bg-transparent font-semantic-input font-[number:var(--semantic-input-font-weight)] text-clarotextos-03 text-[length:var(--semantic-input-font-size)] tracking-[var(--semantic-input-letter-spacing)] leading-[var(--semantic-input-line-height)] [font-style:var(--semantic-input-font-style)] focus-visible:ring-0 focus-visible:ring-offset-0 p-0"
                    placeholder="Pesquise o que você precisa aqui..."
                    value={filter}
                    onChange={(e) => handleFilter({"name": e.target.value})}
                  /> */}
                </div>
              </div>

              {/* <Button onClick={handleNewClick} className="h-11 px-5 py-[15px] bg-claroprimaria-01 rounded-xl">
                <img
                  className="mt-[-2.50px] mb-[-2.50px]"
                  alt="Element"
                  src="/03-5.svg"
                />
                <span className="ml-2 font-inter-16-semi-bold font-[number:var(--inter-16-semi-bold-font-weight)] text-clarobg-202 text-[length:var(--inter-16-semi-bold-font-size)] tracking-[var(--inter-16-semi-bold-letter-spacing)] leading-[var(--inter-16-semi-bold-line-height)] whitespace-nowrap [font-style:var(--inter-16-semi-bold-font-style)]">
                  Produto
                </span>
              </Button> */}
            </div>

            {/* Product Cards */}
            <Card className="w-full bg-clarofrente-01 rounded-2xl overflow-hidden border-0 shadow-none">
              <CardContent className="flex flex-col h-[746px] p-0 relative">
                <ScrollArea className="w-full h-full">
                  {loading && !isInitialLoad ? (
                    <ProductsGridSkeleton />
                  ) : (
                    <div
                      className="grid auto-rows-[364px] gap-[20px] p-[25px]"
                      style={{
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(256px, 1fr))",
                        gridAutoFlow: "dense",
                      }}
                    >
                      {filteredProducts.map((card, index) => (
                        <Card
                          key={card.id}
                          className={`flex flex-col h-full rounded-xl overflow-hidden border border-solid cursor-pointer ${
                            selectedCardId === card.id
                              ? "border-[#1776cf]"
                              : "border-[#d8e0f0]"
                          }`}
                          onClick={() => handleCardSelection(card.id)}
                        >
                          <CardHeader
                            className={`h-[46px] flex w-full items-center justify-center px-0 py-3.5 bg-clarofrente-01 rounded-[12px_12px_0px_0px] border-b-[0.8px] [border-bottom-style:solid] ${
                              selectedCardId === card.id
                                ? "border-[#1776cf]"
                                : "border-[#d8e0f0]"
                            } p-0`}
                          >
                            {card.id === "geral" ? (
                              <img
                                className="flex-1 mt-[-14.00px] mb-[-14.00px]"
                                alt="Cone"
                                src="/-cone-23.svg"
                              />
                            ) : (
                              <div className="flex w-full items-center justify-between px-2.5">
                                <div className="font-inter-16-medium font-[number:var(--inter-16-medium-font-weight)] text-clarotextos-02 text-[length:var(--inter-16-medium-font-size)] whitespace-nowrap tracking-[var(--inter-16-medium-letter-spacing)] leading-[var(--inter-16-medium-line-height)] [font-style:var(--inter-16-medium-font-style)]">
                                  {String(index).padStart(2, "0")}
                                </div>
                                <div className="inline-flex h-[18px] items-center justify-center gap-5">
                                  {card.id !== "geral" &&
                                    items.find((p) => p._id === card.id) &&
                                    findFirstFormActivity(
                                      items.find((p) => p._id === card.id)!
                                    ) && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <svg
                                              className={`cursor-pointer ${
                                                linkCopied === card.id
                                                  ? "opacity-50"
                                                  : ""
                                              } w-[18px] h-[18px]`}
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                              onClick={(e) => {
                                                e.stopPropagation(); // Evita acionar o onClick do Card
                                                const product = items.find(
                                                  (p) => p._id === card.id
                                                );
                                                if (product)
                                                  handleGenerateLink(product);
                                              }}
                                            >
                                              <path
                                                d="M10 13C10.4295 13.5741 10.9774 14.0491 11.6066 14.3929C12.2358 14.7367 12.9315 14.9411 13.6467 14.9913C14.3618 15.0415 15.0796 14.9364 15.7517 14.6828C16.4238 14.4291 17.0338 14.0329 17.54 13.52L20.54 10.52C21.4508 9.59695 21.9548 8.33397 21.9436 7.02299C21.9324 5.71201 21.4068 4.45794 20.479 3.53004C19.5511 2.60214 18.297 2.07659 16.986 2.06537C15.675 2.05415 14.4121 2.55821 13.489 3.46904L11.639 5.31004"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                              <path
                                                d="M14 11C13.5705 10.4259 13.0226 9.95085 12.3934 9.60705C11.7642 9.26325 11.0685 9.05888 10.3534 9.00868C9.63821 8.95849 8.92041 9.06363 8.24826 9.31723C7.57612 9.57082 6.96609 9.96703 6.46 10.48L3.46 13.48C2.54917 14.403 2.04511 15.666 2.05633 16.977C2.06755 18.288 2.5931 19.5421 3.521 20.47C4.4489 21.3979 5.70297 21.9234 7.01395 21.9346C8.32493 21.9458 9.58791 21.4418 10.511 20.531L12.361 18.69"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>Copiar link do formulário</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  <img
                                    className="cursor-pointer"
                                    alt="Edit"
                                    src="/01.svg"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Evita acionar o onClick do Card
                                      handleEditClick(card.id);
                                    }}
                                  />
                                  <img
                                    className=""
                                    alt="Element"
                                    src="/02-1.svg"
                                  />
                                  <img
                                    className="cursor-pointer"
                                    alt="Delete"
                                    src="/03.svg"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Evita acionar o onClick do Card
                                      handleDeleteClick(card.id, card.title);
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </CardHeader>

                          <CardContent className="p-0 h-[318px] flex flex-col">
                            <div className="flex items-center gap-2.5 p-2.5">
                              <div className="flex items-center justify-center gap-2.5 flex-1">
                                <div className="font-inter-14-regular font-[number:var(--inter-14-regular-font-weight)] text-clarotextos-01 text-[length:var(--inter-14-regular-font-size)] tracking-[var(--inter-14-regular-letter-spacing)] leading-[var(--inter-14-regular-line-height)] [font-style:var(--inter-14-regular-font-style)]">
                                  {card.title}
                                </div>
                              </div>
                            </div>

                            <div className="px-3">
                              <div
                                className="w-full h-[127px] rounded-md bg-cover bg-center"
                                style={{
                                  backgroundImage: `url(${card.image})`,
                                }}
                              />
                            </div>

                            <ScrollArea className="flex-1 w-full" type="scroll">
                              <div className="flex flex-col pt-0 pb-[5px] px-[15px] rounded-[0px_0px_8px_8px]">
                                {card.id !== "geral" && (
                                  <>
                                    <div className="flex items-center gap-2.5 px-0 py-2.5 w-full hover:bg-[#f4f4f4] rounded-[6px] transition-colors duration-200 cursor-pointer">
                                      <img
                                        className="w-3.5 h-3.5"
                                        alt="Clock"
                                        src="/-cone-2.svg"
                                      />
                                      <div className="flex items-center gap-2.5 flex-1">
                                        <div className="font-inter-14-regular text-clarotextos-01">
                                          {card.hours}
                                        </div>
                                      </div>
                                      <div className="font-inter-14-regular text-clarotextos-01">
                                        {card.value}
                                      </div>
                                    </div>
                                    <div className="w-full">
                                      <img
                                        className="h-px w-full"
                                        alt="Linha"
                                        src="/linha.svg"
                                      />
                                    </div>
                                  </>
                                )}
                                {card.stats.map((stat) => (
                                  <div
                                    key={`${card.id}-${stat.name}`}
                                    className="flex items-center gap-2.5 px-0 py-2.5 w-full hover:bg-[#f4f4f4] rounded-[6px] transition-colors duration-200 cursor-pointer"
                                  >
                                    <img
                                      className={`${
                                        stat.icon.includes("cone-30")
                                          ? "w-[17.2px] h-[14.2px] ml-[-1.60px]"
                                          : stat.icon.includes("cone.svg")
                                          ? "w-[16.3px] h-[14.3px] ml-[-1.15px]"
                                          : stat.icon.includes("cone-4")
                                          ? "w-3.5 h-[14.3px]"
                                          : ""
                                      }`}
                                      alt="Icon"
                                      src={stat.icon}
                                    />
                                    <div className="flex items-center gap-2.5 flex-1">
                                      <div className="font-inter-14-regular text-clarotextos-01">
                                        {stat.name}
                                      </div>
                                    </div>
                                    <div className="font-inter-14-regular text-clarotextos-01">
                                      {stat.count}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              <ScrollBar
                                orientation="vertical"
                                className="bg-clarobarra-de-rolagem02"
                              />
                            </ScrollArea>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                  <ScrollBar orientation="vertical" />
                </ScrollArea>
              </CardContent>
            </Card>
          </>
        );
    }
  }, [
    selectedTab,
    filter,
    loading,
    isInitialLoad,
    filteredProducts,
    handleFilter,
    selectedCardId,
    handleCardSelection,
    handleDeleteClick,
    PhasesTabContent,
    ActivitiesTabContent,
  ]);

  // Rendering conditional - show full skeleton during initial load
  if (loading && isInitialLoad) {
    return <OutrosByAnimaSkeleton />;
  }

  const handleDisabledTab = (tab: any, type = "tab") => {
    if (type == "tab") {
      if (selectedCardId == "" && tab.name != "Produtos") {
        return true;
      } else if (
        selectedPhaseFilter == "" &&
        tab.name != "Fases" &&
        tab.name != "Produtos"
      ) {
        return true;
      } else if (
        selectedCardActivityId == "" &&
        tab.name != "Atividades" &&
        tab.name != "Fases" &&
        tab.name != "Produtos"
      ) {
        return true;
      }
    } else if (type == "right") {
      if (selectedCardId == "" && tab.name == "Produtos") {
        return true;
      } else if (selectedPhaseFilter == "" && tab.name == "Fases") {
        return true;
      } else if (selectedCardActivityId == "" && tab.name == "Atividades") {
        return true;
      }
    }

    return false;
  };

  return (
    <>
      <section className="flex flex-col w-full items-center  bg-clarofundo-01 relative">
        {/* Header */}
        <header className="flex flex-col items-start justify-center gap-2.5 pl-2.5 pr-0 py-2.5 w-full">
          <h1 className="text-2xl font-bold">{selectedProduct}</h1>

          <h2 className="w-fit font-inter-20-medium font-[number:var(--inter-20-medium-font-weight)] text-clarotextos-02 text-[length:var(--inter-20-medium-font-size)] text-center tracking-[var(--inter-20-medium-letter-spacing)] leading-[var(--inter-20-medium-line-height)] whitespace-nowrap [font-style:var(--inter-20-medium-font-style)]">
            {selectedPhase} / {selectedActivity}
          </h2>
          {/* <h2 className="w-fit font-inter-20-medium font-[number:var(--inter-20-medium-font-weight)] text-clarotextos-02 text-[length:var(--inter-20-medium-font-size)] text-center tracking-[var(--inter-20-medium-letter-spacing)] leading-[var(--inter-20-medium-line-height)] whitespace-nowrap [font-style:var(--inter-20-medium-font-style)]">
            {selectedTab === "Fases" 
              ? selectedCardId === "geral" 
                ? "Todas as Fases" 
                : `Fases de ${selectedCard?.title || ""}`
              : selectedTab === "Atividades"
                ? selectedCardId === "geral"
                  ? selectedPhaseFilter === "todas"
                    ? "Todas as Atividades"
                    : `Atividades da fase ${availablePhases.find(p => p.id === selectedPhaseFilter)?.title || ""}`
                  : selectedPhaseFilter === "todas"
                    ? `Atividades de ${selectedCard?.title || ""}`
                    : `Atividades da fase ${availablePhases.find(p => p.id === selectedPhaseFilter)?.title || ""} de ${selectedCard?.title || ""}`
                : subtitleText
            }
          </h2> */}
        </header>

        {/* Navigation Tabs */}
        <Tabs
          value={selectedTab}
          onValueChange={(value) => {
            setSelectedTab(value);
            // Se estiver mudando para a aba Atividades, redefinir o filtro de fase
            // if (value === "Atividades") {
            //   setSelectedPhaseFilter("todas");
            // }
          }}
          className="w-full bg-clarofrente-01 rounded-xl shadow-[0px_2px_5px_#676e7614,0px_1px_1px_#0000001f] my-[15px]"
          style={{ background: "white" }}
        >
          <div className="flex items-center gap-5 px-2.5">
            <Button
              variant="ghost"
              size="icon"
              className="p-0 min-w-[30px]"
              onClick={() => handleScroll("left")}
            >
              <img
                className="w-[30px] h-[25px]"
                alt="Seta esquerda"
                src="/seta.svg"
              />
            </Button>

            <div className="flex-1 overflow-hidden" ref={scrollContainerRef}>
              <TabsList className="flex w-full gap-2.5 bg-transparent h-auto overflow-x-auto scrollbar-hide">
                {navTabs.map((tab) => (
                  <TabsTrigger
                    key={tab.name}
                    value={tab.name}
                    disabled={handleDisabledTab(tab)}
                    style={{
                      color: selectedTab === tab.name ? "#1777CF" : "grey",
                    }}
                    className={`p-2.5 whitespace-nowrap font-inter-16-regular font-[number:var(--inter-16-regular-font-weight)] text-[length:var(--inter-16-regular-font-size)] tracking-[var(--inter-16-regular-letter-spacing)] leading-[var(--inter-16-regular-line-height)] [font-style:var(--inter-16-regular-font-style)] ${
                      selectedTab === tab.name
                        ? "text-[#1777CF]"
                        : "text-grey-light"
                    } data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#1777CF]`}
                  >
                    {tab.name}
                    {tab.count ? ` (${tab.count})` : ""}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <Button
              variant="ghost"
              size="icon"
              disabled={handleDisabledTab({ name: selectedTab }, "right")}
              className="p-0 min-w-[30px]"
              onClick={() => handleScroll("right")}
            >
              <img
                className="w-[30px] h-[25px]"
                alt="Seta direita"
                src="/seta-1.svg"
              />
            </Button>
          </div>
        </Tabs>

        {/* Main Content */}
        <div className="flex flex-col items-start gap-5 w-full flex-1">
          {renderTabContent()}
        </div>

        {/* Close button */}
        {/* <button className="absolute w-11 h-11 top-[27px] right-[27px]">
          <img alt="Fechar x" src="/fechar--x-.svg" />
        </button> */}

        {/* <div className="flex justify-end">
          <Button onClick={handleNewClick} className="flex items-center gap-2">
            <div className="font-bold">Novo</div>
            <img
              className="w-4 h-4"
              alt="AnimaIcon"
              src="/anima-icon-plus.svg"
            />
          </Button>
        </div> */}
      </section>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationAlert
        isOpen={!!deleteId}
        itemName={deleteItemName}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      {/* WhatsApp Modal */}
      <Dialog open={phoneModalOpen} onOpenChange={handleClosePhoneModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Enviar WhatsApp</DialogTitle>
            <DialogDescription>
              Insira uma lista de números de telefone separados por vírgula.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="phone" className="text-sm font-medium">
                Números de Telefone
              </Label>
              <Textarea
                id="phone"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="Ex: 11987654321, 21987654321, +5511987654321"
                className="min-h-[100px]"
              />
              <p className="text-xs text-muted-foreground">
                Digite apenas DDD+NÚMERO (ex: 11987654321) ou com código do país
                (ex: +5511987654321). O código +55 (Brasil) será adicionado
                automaticamente se não for informado.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClosePhoneModal}>
              Cancelar
            </Button>
            <Button onClick={handlePhoneSubmit}>Enviar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
